/**
 * SpecialEventBookingsPage — Admin bookings list for a Special Event
 *
 * Route: /admin/special-events/bookings
 * Roles: super_admin | manager | reception
 *
 * Features:
 *   - Search by name / phone / order id / invite code
 *   - Filter by status (confirmed / pending / cancelled / checked_in)
 *   - Header summary tiles (confirmed / checked-in / pending / balances)
 *   - Row actions: view details, cancel, mark balance paid, undo check-in
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Search, Ticket, CheckCircle2, XCircle, RefreshCw, Sparkles,
  Wallet, Users, Clock, DoorOpen, Loader2, Phone, ChevronRight, X,
} from 'lucide-react';

import AdminShell from '../../components/admin/AdminShell';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { adminApi } from '../../lib/api';

const SLUG = 'nazakat-shararat';
const fmtINR = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      dateStyle: 'medium', timeStyle: 'short',
    });
  } catch (_e) { return iso; }
};

function StatusPill({ booking }) {
  if (booking.status === 'cancelled') {
    return <Badge className="bg-red-500/15 text-red-300 border border-red-500/30" data-testid={`row-status-${booking.id}`}>Cancelled</Badge>;
  }
  if (booking.checked_in) {
    return <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid={`row-status-${booking.id}`}>Checked-in</Badge>;
  }
  if (booking.status === 'confirmed') {
    return <Badge className="bg-amber-500/15 text-amber-200 border border-amber-500/30" data-testid={`row-status-${booking.id}`}>Confirmed · Awaiting</Badge>;
  }
  if (booking.status === 'pending') {
    return <Badge className="bg-slate-500/15 text-slate-300 border border-slate-500/30" data-testid={`row-status-${booking.id}`}>Pending</Badge>;
  }
  return <Badge className="bg-zinc-500/15 text-zinc-300 border border-zinc-500/30" data-testid={`row-status-${booking.id}`}>{booking.status}</Badge>;
}

function KpiTile({ icon: Icon, label, value, tone = 'gold', testid }) {
  const toneMap = {
    gold:    'border-[#D4A843]/25 bg-[#D4A843]/8 text-[#F1E2B8]',
    emerald: 'border-emerald-500/25 bg-emerald-500/8 text-emerald-200',
    amber:   'border-amber-500/25 bg-amber-500/8 text-amber-200',
    slate:   'border-slate-500/25 bg-slate-500/10 text-slate-200',
  };
  return (
    <div className={`rounded-lg border p-4 ${toneMap[tone] || toneMap.gold}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className="opacity-80" />
        <span className="text-[10px] uppercase tracking-widest opacity-80">{label}</span>
      </div>
      <p className="text-xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

export default function SpecialEventBookingsPage() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [cancelPromptFor, setCancelPromptFor] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 200 };
      if (status !== 'all') params.status = status;
      if (search.trim()) params.q = search.trim();
      const res = await adminApi.sxeAdminListBookings(SLUG, params);
      setRows(res.data.bookings || []);
      setTotal(res.data.total || 0);
      setSummary(res.data.summary || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load bookings.');
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  useEffect(() => { load(); }, [load]);

  const actOn = async (booking, action, extra = {}) => {
    try {
      setDetailBusy(true);
      const res = await adminApi.sxeAdminPatchBooking(SLUG, booking.id, { action, ...extra });
      toast.success(
        action === 'cancel'             ? 'Booking cancelled.' :
        action === 'mark_balance_paid'  ? 'Balance marked as collected.' :
        action === 'checkin'            ? 'Checked-in successfully.' :
        action === 'undo_checkin'       ? 'Check-in reverted.' : 'Updated.',
      );
      if (detail && detail.id === booking.id && res?.data?.booking) {
        setDetail(res.data.booking);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed.');
    } finally {
      setDetailBusy(false);
    }
  };

  const submitCancel = async () => {
    if (!cancelPromptFor) return;
    await actOn(cancelPromptFor, 'cancel', { note: cancelReason.trim() || 'Admin cancelled' });
    setCancelPromptFor(null);
    setCancelReason('');
  };

  const kpis = useMemo(() => summary || {
    confirmed: 0, checked_in: 0, pending_checkin: 0,
    total_paid: 0, total_due_at_gate: 0, balance_collected: 0, balance_outstanding: 0,
  }, [summary]);

  return (
    <AdminShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="sxe-bookings-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-[#D4A843]" />
              <h1 className="text-xl font-semibold text-white">Nazakat &amp; Shararat — Bookings</h1>
            </div>
            <p className="text-sm text-zinc-400 mt-1">
              {total} total bookings
            </p>
          </div>
          <Button
            variant="outline"
            className="border-zinc-800 text-zinc-200 hover:bg-zinc-900"
            onClick={load}
            data-testid="sxe-bookings-refresh"
          >
            <RefreshCw size={14} className="mr-2" /> Refresh
          </Button>
        </div>

        {/* KPI Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiTile icon={Ticket}       label="Confirmed"        value={kpis.confirmed}                        tone="gold"    testid="kpi-confirmed" />
          <KpiTile icon={DoorOpen}     label="Checked-in"       value={kpis.checked_in}                       tone="emerald" testid="kpi-checked-in" />
          <KpiTile icon={Clock}        label="Pending Check-in" value={kpis.pending_checkin}                  tone="amber"   testid="kpi-pending" />
          <KpiTile icon={Wallet}       label="Balance Outstanding" value={fmtINR(kpis.balance_outstanding)}   tone="slate"   testid="kpi-balance-outstanding" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <KpiTile icon={Wallet} label="Total Paid Online"    value={fmtINR(kpis.total_paid)}         tone="gold"    testid="kpi-paid" />
          <KpiTile icon={Wallet} label="Total Due at Gate"    value={fmtINR(kpis.total_due_at_gate)}  tone="amber"   testid="kpi-due-at-gate" />
          <KpiTile icon={Wallet} label="Balance Collected"    value={fmtINR(kpis.balance_collected)}  tone="emerald" testid="kpi-balance-collected" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              placeholder="Search name, phone, order or invite code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100"
              data-testid="sxe-bookings-search"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-56 bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="sxe-bookings-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="confirmed">Confirmed (awaiting)</SelectItem>
              <SelectItem value="checked_in">Checked-in</SelectItem>
              <SelectItem value="pending">Pending payment</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden">
          {loading ? (
            <div className="p-12 flex items-center justify-center text-zinc-500">
              <Loader2 size={22} className="animate-spin mr-2 text-[#D4A843]" /> Loading bookings…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-zinc-500" data-testid="sxe-bookings-empty">
              <Ticket size={28} className="mx-auto mb-2 opacity-40" />
              No bookings match the current filter.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                    <th className="text-left px-4 py-3">Customer</th>
                    <th className="text-left px-4 py-3">Order</th>
                    <th className="text-left px-4 py-3">Items</th>
                    <th className="text-right px-4 py-3">Total</th>
                    <th className="text-right px-4 py-3">Paid</th>
                    <th className="text-right px-4 py-3">Due at Gate</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Booked</th>
                    <th className="text-right px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => {
                    const dueRemaining = Math.max(0, (b.pay_at_gate || 0) - (b.balance_collected || 0));
                    return (
                      <tr key={b.id} className="border-b border-zinc-900 hover:bg-zinc-900/40 transition-colors" data-testid={`sxe-booking-row-${b.id}`}>
                        <td className="px-4 py-3">
                          <div className="text-zinc-100 font-medium">{b.customer_name || '—'}</div>
                          <div className="text-xs text-zinc-500 flex items-center gap-1">
                            <Phone size={10} /> {b.customer_phone_e164 || '—'}
                          </div>
                          {b.invite_code && (
                            <div className="text-[10px] text-[#D4A843] mt-0.5">code · {b.invite_code}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-[#D4A843]">{b.payment_order_id}</td>
                        <td className="px-4 py-3 text-zinc-300 text-xs">
                          {(b.items || []).map((it) => `${it.sku_id} × ${it.qty}`).join(' · ') || '—'}
                        </td>
                        <td className="px-4 py-3 text-right text-zinc-100 tabular-nums">{fmtINR(b.total)}</td>
                        <td className="px-4 py-3 text-right text-emerald-300 tabular-nums">{fmtINR(b.pay_now)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {dueRemaining > 0 ? (
                            <span className="text-amber-200">{fmtINR(dueRemaining)}</span>
                          ) : (
                            <span className="text-emerald-300">Cleared</span>
                          )}
                        </td>
                        <td className="px-4 py-3"><StatusPill booking={b} /></td>
                        <td className="px-4 py-3 text-zinc-500 text-xs">{fmtDateTime(b.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                            onClick={() => setDetail(b)}
                            data-testid={`sxe-booking-open-${b.id}`}
                          >
                            View <ChevronRight size={14} className="ml-1" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Detail / Actions Dialog ───────────────────────────────── */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg max-h-[92vh] overflow-y-auto" data-testid="sxe-booking-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Sparkles size={16} className="text-[#D4A843]" /> Booking {detail.payment_order_id}
                </DialogTitle>
                <DialogDescription className="text-zinc-500">
                  {detail.customer_name} · {detail.customer_phone_e164}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-2 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-zinc-500">Status</span>
                  <StatusPill booking={detail} />
                </div>

                {/* Amount panel */}
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3 text-sm">
                  <div className="flex justify-between py-0.5">
                    <span className="text-zinc-400">Total</span>
                    <span className="tabular-nums">{fmtINR(detail.total)}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-zinc-400">Paid online</span>
                    <span className="tabular-nums text-emerald-300">{fmtINR(detail.pay_now)}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-zinc-400">Due at gate</span>
                    <span className="tabular-nums text-amber-200">{fmtINR(detail.pay_at_gate)}</span>
                  </div>
                  {detail.balance_collected > 0 && (
                    <div className="flex justify-between py-0.5 border-t border-zinc-800 pt-1 mt-1">
                      <span className="text-zinc-400">Balance collected</span>
                      <span className="tabular-nums text-emerald-300">
                        {fmtINR(detail.balance_collected)}
                        {detail.balance_paid_method && ` (${detail.balance_paid_method})`}
                      </span>
                    </div>
                  )}
                  {detail.balance_paid_at && (
                    <div className="text-[10px] text-zinc-500 mt-0.5">Collected at {fmtDateTime(detail.balance_paid_at)}</div>
                  )}
                </div>

                {/* Items */}
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Items</p>
                  <div className="space-y-1 text-sm">
                    {(detail.items || []).map((it) => (
                      <div key={it.sku_id} className="flex justify-between text-zinc-200">
                        <span>{it.label || it.sku_id} × {it.qty}</span>
                        <span className="tabular-nums">{fmtINR(it.line_total)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Meta */}
                <div className="grid grid-cols-2 gap-2 text-xs text-zinc-400">
                  <div>Booked · {fmtDateTime(detail.created_at)}</div>
                  <div>Invite · <span className="text-[#D4A843]">{detail.invite_code || '—'}</span></div>
                  {detail.checked_in && (
                    <div className="col-span-2 text-emerald-300">
                      Checked-in at {fmtDateTime(detail.checked_in_at)} by {detail.checked_in_by}
                    </div>
                  )}
                  {detail.status === 'cancelled' && (
                    <div className="col-span-2 text-red-300">
                      Cancelled at {fmtDateTime(detail.cancelled_at)} — {detail.cancel_reason || 'no reason'}
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="flex flex-wrap gap-2 pt-2">
                {detail.status === 'confirmed' && !detail.checked_in && (
                  <Button
                    className="bg-emerald-500 hover:bg-emerald-500/90 text-black font-medium"
                    disabled={detailBusy}
                    onClick={() => actOn(detail, 'checkin')}
                    data-testid="sxe-booking-checkin-btn"
                  >
                    <CheckCircle2 size={14} className="mr-1.5" /> Mark checked-in
                  </Button>
                )}
                {detail.checked_in && (
                  <Button
                    variant="outline"
                    className="border-zinc-700 text-zinc-200 hover:bg-zinc-900"
                    disabled={detailBusy}
                    onClick={() => actOn(detail, 'undo_checkin')}
                    data-testid="sxe-booking-undo-checkin-btn"
                  >
                    Undo check-in
                  </Button>
                )}
                {detail.status === 'confirmed' && detail.pay_at_gate > 0 && !detail.balance_paid_at && (
                  <Button
                    variant="outline"
                    className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                    disabled={detailBusy}
                    onClick={() => actOn(detail, 'mark_balance_paid', { payment_method: 'cash' })}
                    data-testid="sxe-booking-mark-balance-btn"
                  >
                    <Wallet size={14} className="mr-1.5" /> Mark balance paid ({fmtINR(detail.pay_at_gate)})
                  </Button>
                )}
                {detail.status === 'confirmed' && !detail.checked_in && (
                  <Button
                    variant="outline"
                    className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                    disabled={detailBusy}
                    onClick={() => { setCancelPromptFor(detail); setCancelReason(''); }}
                    data-testid="sxe-booking-cancel-btn"
                  >
                    <XCircle size={14} className="mr-1.5" /> Cancel
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Cancel confirmation dialog ─────────────────────────────── */}
      <Dialog open={!!cancelPromptFor} onOpenChange={(o) => !o && setCancelPromptFor(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-sm" data-testid="sxe-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Cancel this booking?</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Inventory will be released. This cannot be undone from the UI.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Reason (optional)</label>
            <Input
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
              placeholder="e.g. Guest requested refund"
              data-testid="sxe-cancel-reason-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setCancelPromptFor(null)}
              className="text-zinc-300 hover:bg-zinc-900"
              data-testid="sxe-cancel-abort-btn"
            >
              Keep booking
            </Button>
            <Button
              className="bg-red-500 hover:bg-red-500/90 text-white"
              disabled={detailBusy}
              onClick={submitCancel}
              data-testid="sxe-cancel-confirm-btn"
            >
              <X size={14} className="mr-1.5" /> Cancel booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
